import React, { FC, useCallback, useEffect } from 'react';
import { Box, Collapse, Spacer, Text, useDisclosure } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import useToast from 'components/ToastProvider/useToast';
import type { DetailedTransactionReceipt } from 'features/Bank/pages/Overview/Sidebar/hooks/useDetailedTransactionQuery';
import logger from 'helpers/logger';

import { useFormContext } from '../../form';
import { VatMode } from '../../types';
import VatAmountForm from '../VatAmountForm';
import AddVatFormActions from './components/AddVatFormActions';
import { formatInput } from './helpers';
import useCreateVatAmount from './hooks/mutations/useCreateVatAmount';
import useUpdateVatAmount from './hooks/mutations/useUpdateVatAmount';
import locales from './locales';

type AddVatProps = {
  mode: VatMode;
  onFormClosed: () => void;
  receipt: DetailedTransactionReceipt;
  transactionTotal: number;
} & React.ComponentProps<typeof SunshineCard>;

const AddVat: FC<AddVatProps> = ({
  mode,
  onFormClosed,
  receipt,
  transactionTotal,
  ...sunshineCardProps
}) => {
  const { showToast } = useToast();
  const { companyProfileId } = useCompanyContext();
  const [createVatAmount, { loading: isCreating }] = useCreateVatAmount();
  const [updateVatAmount, { loading: isUpdating }] = useUpdateVatAmount();

  const {
    isOpen: showVatForm,
    onClose: closeCollapse,
    onOpen: openCollapse,
  } = useDisclosure();

  const { getValues } = useFormContext();

  const closeForm = useCallback(() => {
    if (mode === 'edition') {
      onFormClosed();
    } else {
      closeCollapse();
    }
  }, [mode, onFormClosed, closeCollapse]);

  // necessary to trigger the Collapse opening animation when open by default
  useEffect(() => {
    if (mode === 'edition') {
      setTimeout(() => {
        openCollapse();
      }, 0);
    }
  }, [mode, openCollapse]);

  const onValidateClicked = async () => {
    const rows = getValues('rows');
    const vatItemsInput = formatInput(rows);

    try {
      if (!companyProfileId) {
        throw new Error('companyProfileId is undefined in AddVat.');
      }

      if (!receipt) {
        throw new Error('No receipt for this transaction.');
      }

      if (receipt.vatAmount) {
        const res = await updateVatAmount({
          variables: {
            input: {
              vatAmountId: receipt.vatAmount.vatAmountId,
              vatAmountItems: vatItemsInput,
            },
          },
        });

        const newVatAmountItems = res.data?.updateVatAmount.vatAmountItems;

        if (newVatAmountItems && newVatAmountItems.length > 0) {
          showToast({ message: locales.successAdd, type: 'success' });
          closeForm();
        } else {
          showToast({ message: locales.successDelete, type: 'success' });
          closeCollapse();
        }
      } else {
        await createVatAmount({
          variables: {
            input: {
              companyProfileId,
              receiptId: receipt.receiptId,
              vatAmountItems: vatItemsInput,
            },
          },
        });
        showToast({ message: locales.successAdd, type: 'success' });
        closeForm();
      }
    } catch (err) {
      showToast({ message: locales.error, type: 'error' });
      logger.error(err);
    }
  };

  const isEditingMode = mode === 'edition';
  const isEditing = isEditingMode || showVatForm;

  return (
    <SunshineCard {...sunshineCardProps}>
      <SunshineCard.Content>
        <SunshineCard.Title
          alignItems="center"
          display="flex"
          height="24px" // Use a fixed height to prevent UI shifts when showing/hiding the form
          width="full"
        >
          <Text fontWeight="weight-400">{locales.title}</Text>

          <Spacer />

          <AddVatFormActions
            isEditing={isEditing}
            isLoading={isCreating || isUpdating}
            onCancel={closeForm}
            onSave={onValidateClicked}
          />
        </SunshineCard.Title>

        {showVatForm ? null : (
          <SunshineCard
            alignItems="center"
            display="flex"
            marginTop="space-16"
            onClick={openCollapse}
          >
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="percentage" />
            </SunshineCard.Slot>

            <SunshineCard.Content display="flex" justifyContent="center">
              <SunshineCard.Title>{locales.addVatRate}</SunshineCard.Title>
            </SunshineCard.Content>

            <SunshineCard.Slot name="action">
              <SunshineIcon name="chevron-right" size="icon-24" />
            </SunshineCard.Slot>
          </SunshineCard>
        )}

        <Collapse in={isEditingMode || showVatForm}>
          <Box marginTop="space-16">
            <VatAmountForm
              alignItems="center"
              transactionAmount={transactionTotal}
            />
          </Box>
        </Collapse>
      </SunshineCard.Content>
    </SunshineCard>
  );
};

export default AddVat;
